/**
 * การจัดการเวลา
 *
 * กติกาเดียวของระบบ: เก็บเป็น UTC รูปแบบ ISO-8601 ลงท้ายด้วย Z เสมอ
 * แปลงเป็น Asia/Bangkok เฉพาะตอนแสดงผลและตอน export เท่านั้น
 */
import { DISPLAY_TIME_ZONE } from './config';

export function nowIso(): string {
  return new Date().toISOString();
}

export function toIso(value: Date | number | string): string {
  return new Date(value).toISOString();
}

export function isIsoUtc(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}([.][0-9]{1,3})?Z$/.test(value);
}

const dateTimeFormatter = new Intl.DateTimeFormat('th-TH', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const timeFormatter = new Intl.DateTimeFormat('th-TH', {
  timeZone: DISPLAY_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const dateFormatter = new Intl.DateTimeFormat('th-TH', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** เช่น "18 ส.ค. 2569 09:43" */
export function formatDateTimeTh(value: string | number | Date): string {
  return dateTimeFormatter.format(new Date(value));
}

/** เช่น "09:43:58" */
export function formatTimeTh(value: string | number | Date): string {
  return timeFormatter.format(new Date(value));
}

/** เช่น "18/08/2569" */
export function formatDateTh(value: string | number | Date): string {
  return dateFormatter.format(new Date(value));
}

/** เช่น "4 วินาทีที่แล้ว" — ใช้บอกอายุของพิกัด */
export function formatAgo(fromMs: number, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - fromMs) / 1000));
  if (seconds < 60) return seconds + ' วินาทีที่แล้ว';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes + ' นาทีที่แล้ว';
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours + ' ชั่วโมงที่แล้ว';
  return Math.round(hours / 24) + ' วันที่แล้ว';
}

const csvFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * แปลง ISO UTC เป็นเวลาไทยแบบอ่านง่ายสำหรับใส่ในไฟล์ CSV
 * เช่น "2026-08-18 16:43:58" (เวลาไทย ไม่มีสัญลักษณ์โซนเวลา)
 */
export function toBangkokCsvTimestamp(isoUtc: string): string {
  const parts = csvFormatter.formatToParts(new Date(isoUtc));
  const get = (type: string): string => {
    const found = parts.find((part) => part.type === type);
    return found ? found.value : '00';
  };
  const date = get('year') + '-' + get('month') + '-' + get('day');
  const time = get('hour') + ':' + get('minute') + ':' + get('second');
  return date + ' ' + time;
}

export function minutesSince(iso: string, now = Date.now()): number {
  return (now - new Date(iso).getTime()) / 60000;
}
